import { normalizeCodeLanguage } from '@lexical/code';

const MIN_CONFIDENCE_SCORE = 3;
const MIN_CONFIDENCE_DELTA = 2;

function getTopScoringLanguage(scores) {
  let topLanguage = null;
  let topScore = 0;
  let secondScore = 0;

  Object.entries(scores).forEach(([language, score]) => {
    if (score > topScore) {
      secondScore = topScore;
      topScore = score;
      topLanguage = language;
      return;
    }

    if (score > secondScore) {
      secondScore = score;
    }
  });

  if (!topLanguage || topScore < MIN_CONFIDENCE_SCORE) return null;
  if (topScore < secondScore + MIN_CONFIDENCE_DELTA && topScore < 6) return null;
  return topLanguage;
}

function addScore(scores, language, points) {
  scores[language] = (scores[language] || 0) + points;
}

export function detectCodeLanguage(rawCode) {
  if (typeof rawCode !== 'string') return null;

  const code = rawCode.trim();
  if (code.length < 12) return null;

  const scores = {};

  // Markup / HTML / XML
  if (/<!doctype html>|<html[\s>]|<\/(html|head|body|div|span|script|style)>/i.test(code)) {
    addScore(scores, 'html', 6);
  }
  if (/<\?xml\b|\bxmlns(:\w+)?=|<\/?[a-zA-Z_][\w:.-]*\s*\/?>/.test(code)) {
    addScore(scores, 'xml', 4);
  }
  if (/<[a-zA-Z][\w-]*[\s\S]*>[\s\S]*<\/[a-zA-Z][\w-]*>/.test(code)) {
    addScore(scores, 'html', 2);
  }

  // CSS
  if (/@media\b|@keyframes\b|--[a-z-]+\s*:/.test(code)) {
    addScore(scores, 'css', 5);
  }
  if (/[.#][\w-]+\s*\{[\s\S]*?\}/.test(code) || /[a-z-]+\s*:\s*[^;\n]+;/.test(code)) {
    addScore(scores, 'css', 3);
  }

  // SQL
  if (/\b(select|insert|update|delete|create|alter|drop)\b[\s\S]{0,80}\b(from|into|table|set)\b/i.test(code)) {
    addScore(scores, 'sql', 6);
  }
  if (/\bwhere\b|\bgroup by\b|\border by\b|\bjoin\b/i.test(code)) {
    addScore(scores, 'sql', 2);
  }

  // Python
  if (/^\s*(def|class)\s+\w+\s*(\(|:)/m.test(code)) {
    addScore(scores, 'py', 5);
  }
  if (/\bfrom\s+\w+(\.\w+)*\s+import\b|\bimport\s+\w+/.test(code)) {
    addScore(scores, 'py', 3);
  }
  if (/\bself\b|^\s*elif\b|^\s*except\b|^\s*with\b/m.test(code)) {
    addScore(scores, 'py', 2);
  }
  if (/:\s*(#.*)?$/m.test(code)) {
    addScore(scores, 'py', 1);
  }

  // TypeScript
  if (/\binterface\s+\w+|\btype\s+\w+\s*=|\benum\s+\w+/.test(code)) {
    addScore(scores, 'typescript', 6);
  }
  if (/\b(public|private|protected|readonly)\s+\w+/.test(code)) {
    addScore(scores, 'typescript', 4);
  }
  if (/:\s*(string|number|boolean|unknown|never|void|Record<|Promise<|Array<)/.test(code)) {
    addScore(scores, 'typescript', 4);
  }
  if (/\bimplements\b|\bimport\s+type\b/.test(code)) {
    addScore(scores, 'typescript', 3);
  }

  // JavaScript
  if (/\b(const|let|var)\s+\w+/.test(code)) {
    addScore(scores, 'js', 3);
  }
  if (/=>|console\.log|module\.exports|require\(/.test(code)) {
    addScore(scores, 'js', 2);
  }
  if (/\bfunction\s+\w+\s*\(/.test(code)) {
    addScore(scores, 'js', 2);
  }

  // Java
  if (/\bpublic\s+class\s+\w+/.test(code)) {
    addScore(scores, 'java', 6);
  }
  if (/\bpublic\s+static\s+void\s+main\s*\(/.test(code)) {
    addScore(scores, 'java', 4);
  }
  if (/\bSystem\.out\.println\s*\(/.test(code)) {
    addScore(scores, 'java', 4);
  }
  if (/\bpackage\s+[\w.]+;|\bimport\s+java\./.test(code)) {
    addScore(scores, 'java', 2);
  }

  // C / C++
  if (/^\s*#include\s+[<"][^>"]+[>"]/m.test(code)) {
    addScore(scores, 'c', 3);
    addScore(scores, 'cpp', 3);
  }
  if (/\bstd::|\bcout\s*<<|\bcin\s*>>|\btemplate\s*</.test(code)) {
    addScore(scores, 'cpp', 5);
  }
  if (/\bprintf\s*\(|\bscanf\s*\(|\bmalloc\s*\(/.test(code)) {
    addScore(scores, 'c', 4);
  }
  if (/\bint\s+main\s*\(/.test(code)) {
    addScore(scores, 'c', 2);
    addScore(scores, 'cpp', 2);
  }

  // Rust
  if (/\bfn\s+main\s*\(/.test(code)) {
    addScore(scores, 'rust', 4);
  }
  if (/\blet\s+mut\b|\bprintln!\s*\(|\bmatch\b|\bimpl\b/.test(code)) {
    addScore(scores, 'rust', 3);
  }
  if (/\buse\s+[a-z0-9_:]+::/i.test(code)) {
    addScore(scores, 'rust', 2);
  }

  // Swift
  if (/\bimport\s+Foundation\b/.test(code)) {
    addScore(scores, 'swift', 4);
  }
  if (/\bfunc\s+\w+\s*\(/.test(code)) {
    addScore(scores, 'swift', 3);
  }
  if (/\bguard\s+let\b|\bif\s+let\b|\bvar\s+\w+\s*:\s*\w+/.test(code)) {
    addScore(scores, 'swift', 2);
  }

  // PowerShell
  if (/\bWrite-Host\b|\b(Get|Set|New|Remove)-[A-Z]\w*/.test(code)) {
    addScore(scores, 'powershell', 5);
  }
  if (/\$[A-Za-z_]\w*|\bparam\s*\(/.test(code)) {
    addScore(scores, 'powershell', 3);
  }

  // Markdown
  if (/^#{1,6}\s+\S+/m.test(code)) {
    addScore(scores, 'markdown', 3);
  }
  if (/^\s*[-*+]\s+\S+/m.test(code) && /`{3}/.test(code)) {
    addScore(scores, 'markdown', 2);
  }

  const topLanguage = getTopScoringLanguage(scores);
  if (!topLanguage) return null;

  return normalizeCodeLanguage(topLanguage) || topLanguage;
}

